import { SelectProps } from "@amzn/awsui-components-react";
import { useMemo } from "react";
import { buildingIdFormat, buildingResponse } from "interfaces";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { useBuildingsConfig } from "./useCatalogicalConfig";

const getStringPath = "common.hook.useBuildings";

/**
 * Custom hook that fetches buildings from Catalogical and transforms into select options format
 *
 * @returns {{ options: SelectProps.Options; isLoading: boolean }} Select options and loading state
 */
export const useBuildingsForSelect = (): { options: SelectProps.Options; isLoading: boolean } => {
    const { data: buildings, isLoading } = useBuildingsConfig();

    const options = useMemo(() => {
        if (!buildings) return [];
        return buildings.map((building) => ({
            label: building.building,
            value: JSON.stringify({ region: building.region, country: building.country, city: building.city, building: building.building }),
        }));
    }, [buildings]);

    return { options, isLoading };
};

/**
 * Custom hook that builds CheckboxNode structure with parent-child relationships
 * based on the buildings hierarchy fetched from Catalogical
 *
 * @returns {{ nodes: CheckboxNode[]; isLoading: boolean }} Hierarchy and loading state
 */
export const useBuildingsWithParentChild = (): { nodes: CheckboxNode[]; isLoading: boolean } => {
    const { data: buildings, isLoading } = useBuildingsConfig();

    const nodes = useMemo(() => {
        if (!buildings) return [];
        const hierarchy: Record<string, Record<string, Record<string, string[]>>> = {};

        buildings.forEach((building) => {
            if (!hierarchy[building.region]) {
                hierarchy[building.region] = {};
            }
            if (!hierarchy[building.region][building.country]) {
                hierarchy[building.region][building.country] = {};
            }
            if (!hierarchy[building.region][building.country][building.city]) {
                hierarchy[building.region][building.country][building.city] = [];
            }
            hierarchy[building.region][building.country][building.city].push(building.building);
        });

        const result: CheckboxNode[] = [];

        Object.keys(hierarchy)
            .sort()
            .forEach((region) => {
                const regionNode: CheckboxNode = {
                    id: encodeURIComponent(
                        JSON.stringify({ name: region, type: "region", root: "" } as buildingIdFormat),
                    ),
                    name: region,
                    children: [],
                };

                Object.keys(hierarchy[region])
                    .sort()
                    .forEach((country) => {
                        const countryNode: CheckboxNode = {
                            id: encodeURIComponent(
                                JSON.stringify({ name: country, type: "country", root: region } as buildingIdFormat),
                            ),
                            name: country,
                            parent: regionNode,
                            children: [],
                        };

                        Object.keys(hierarchy[region][country])
                            .sort()
                            .forEach((city) => {
                                const cityNode: CheckboxNode = {
                                    id: encodeURIComponent(
                                        JSON.stringify({
                                            name: city,
                                            type: "city",
                                            root: `${region}/${country}`,
                                        } as buildingIdFormat),
                                    ),
                                    name: city,
                                    parent: countryNode,
                                    children: [],
                                };

                                hierarchy[region][country][city].sort().forEach((building) => {
                                    const buildingNode: CheckboxNode = {
                                        id: encodeURIComponent(
                                            JSON.stringify({
                                                name: building,
                                                type: "building",
                                                root: `${region}/${country}/${city}`,
                                            } as buildingIdFormat),
                                        ),
                                        name: building,
                                        parent: cityNode,
                                    };
                                    cityNode.children!.push(buildingNode);
                                });

                                countryNode.children!.push(cityNode);
                            });

                        regionNode.children!.push(countryNode);
                    });

                result.push(regionNode);
            });

        return result;
    }, [buildings]);

    return { nodes, isLoading };
};

/**
 * All countries option when filtering
 */
export const AllCountriesOption: SelectProps.Option = { label: getString(`${getStringPath}.allCountries`), value: undefined };

/**
 * Custom hook that returns all unique countries from the buildings data
 * to be used in a select dropdown
 *
 * @returns {{ options: SelectProps.Options; isLoading: boolean }} Country options and loading state
 */
export const useCountries = (): { options: SelectProps.Options; isLoading: boolean } => {
    const { data: buildings, isLoading } = useBuildingsConfig();

    const options = useMemo(() => {
        if (!buildings) return [AllCountriesOption];
        const countries = new Set<string>();
        for (const location of buildings) {
            countries.add(location.country);
        }
        return [
            AllCountriesOption,
            ...Array.from(countries).map((country) => ({
                label: country,
                value: country,
            })),
        ];
    }, [buildings]);

    return { options, isLoading };
};
